import { init, plugin } from '@faceup/devtools'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from '@faceup/router'
import {
  maskBreadcrumb,
  maskDynamicSamplingContext,
  maskEventTarget,
  maskRecordingEvent,
  maskSpan,
  registerStaleChunkReload,
  sentryMaskedAttributes,
} from '@faceup/utils'
import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { name, version } from '../package.json'
import { App } from './App'

registerStaleChunkReload()

if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
  init({
    plugins: [
      plugin.requests,
      plugin.cache,
      plugin.httpAnalyzer,
      plugin.console,
      plugin.playground,
      plugin.cleaner,
      plugin.translations,
    ],
    apiUrl: import.meta.env.VITE_API_URL ?? '',
  })
}

if (import.meta.env.VITE_ENVIRONMENT !== 'development') {
  Sentry.init({
    dsn: 'https://539b1161e2d54812b2b065c84f03dc58@o441414.ingest.sentry.io/5431071',
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        mask: ['.sensitive'],
        maskAttributes: sentryMaskedAttributes,
        beforeAddRecordingEvent: maskRecordingEvent,
      }),
    ],
    tracesSampleRate: 1,
    tracePropagationTargets: [import.meta.env.VITE_API_URL ?? ''],
    release: `${name.replace('@faceup/', '')}@${version}`, // after changing release name, it is required to change it in sourcemaps upload as well
    environment: import.meta.env.VITE_ENVIRONMENT ?? '',
    replaysSessionSampleRate: 0.33,
    replaysOnErrorSampleRate: 1.0,
    normalizeDepth: 6,
    beforeBreadcrumb: maskBreadcrumb,
    beforeSend: maskEventTarget,
    beforeSendSpan: maskSpan,
    enableLogs: true,
    // A flaky connection, a navigation that cancels a request, or a browser extension throwing
    // into the page is not something we can fix.
    ignoreErrors: [
      'Failed to fetch',
      'Load failed',
      'NetworkError when attempting to fetch resource',
      'The network connection was lost',
      'AbortError',
      'ResizeObserver loop',
    ],
    denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-(web-)?extension:\/\//],
  })
  Sentry.getClient()?.on('createDsc', (dsc, rootSpan) => {
    maskDynamicSamplingContext(dsc, rootSpan && Sentry.spanToJSON(rootSpan).op)
  })
}

const container = document.getElementById('app')
if (container) {
  const root = createRoot(container)
  root.render(<App />)
}
